import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CameraController, type CameraPorts, type CameraState, type VideoSurface } from './camera'

/**
 * The React end of the camera. This is the only file that touches the real
 * browser APIs; everything with a decision in it lives in CameraController and is
 * tested against fakes.
 *
 * The lifetime rule from ui-design-system.md 10.1 is enforced here and nowhere
 * else: the effect starts the stream when a scanning screen mounts and its
 * cleanup stops every track when it unmounts. There is no path that leaves a
 * camera live behind a closed screen, because there is no other owner.
 */

/** BarcodeDetector is not in lib.dom yet, so name only what we call. */
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): { detect(source: unknown): Promise<Array<{ rawValue: string }>> }
  getSupportedFormats?: () => Promise<string[]>
}

/**
 * The formats a second-hand children's book actually carries: EAN-13 on the back
 * of anything printed since the eighties, plus our own QR and Code 128 tub and
 * unit labels.
 */
export const SCAN_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39', 'qr_code', 'itf']

function detectorCtor(): BarcodeDetectorCtor | null {
  const g = globalThis as { BarcodeDetector?: BarcodeDetectorCtor }
  return typeof g.BarcodeDetector === 'function' ? g.BarcodeDetector : null
}

/** True where the browser can decode in software. False is a supported outcome, not a bug. */
export function cameraDecodeSupported(): boolean {
  return detectorCtor() !== null
}

/** The catalogue still, drawn off the live preview - one tap, no OS camera launch. */
async function captureStill(video: VideoSurface): Promise<Blob | null> {
  const width = video.videoWidth ?? 0
  const height = video.videoHeight ?? 0
  if (!width || !height || typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  ctx.drawImage(video as unknown as CanvasImageSource, 0, 0, width, height)
  return await new Promise<Blob | null>((resolve) => {
    // JPEG at 0.85: a catalogue photo is a listing image, not an archive master,
    // and these upload over the shop's broadband from a phone.
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.85)
  })
}

export interface UseCameraOptions {
  /**
   * Straight into ScanFocusController.decoded, so a camera read and a wireless
   * HID read are the same event downstream. Never give the camera its own path.
   */
  onDecode?: (code: string) => void
  onPhoto?: (photo: Blob) => void
  /** False while no scanning screen is mounted - the camera stays dark. */
  enabled?: boolean
  /** Test seam. Production passes nothing and gets the real browser. */
  ports?: Partial<CameraPorts>
}

export interface UseCameraResult {
  camera: CameraState
  videoRef: React.RefCallback<HTMLVideoElement>
  capturePhoto: () => Promise<Blob | null>
  /** False where BarcodeDetector is missing: preview and photo live, decode does not. */
  decodesInSoftware: boolean
}

export function useCamera({ onDecode, onPhoto, enabled = true, ports }: UseCameraOptions = {}): UseCameraResult {
  const [camera, setCamera] = useState<CameraState>('off')
  // Kept in refs so a re-render with a new callback identity never restarts the
  // stream - a camera that reboots on every keystroke is worse than no camera.
  const decodeRef = useRef(onDecode)
  const photoRef = useRef(onPhoto)
  decodeRef.current = onDecode
  photoRef.current = onPhoto

  const controller = useMemo(() => {
    const ctor = detectorCtor()
    const nav = typeof navigator === 'undefined' ? null : navigator
    return new CameraController({
      mediaDevices: nav?.mediaDevices ? (nav.mediaDevices as unknown as CameraPorts['mediaDevices']) : null,
      createDetector: ctor ? () => new ctor({ formats: SCAN_FORMATS }) : null,
      schedule: (fn) => {
        if (typeof requestAnimationFrame !== 'function') return
        const handle = requestAnimationFrame(() => fn())
        return () => cancelAnimationFrame(handle)
      },
      capture: captureStill,
      onDecode: (code) => decodeRef.current?.(code),
      onChange: setCamera,
      ...ports,
    })
    // Built once per mount. The ports are browser globals, not props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const videoRef = useCallback<React.RefCallback<HTMLVideoElement>>(
    (el) => {
      controller.attach(el)
    },
    [controller],
  )

  useEffect(() => {
    if (!enabled) {
      controller.stop()
      return
    }
    void controller.start()
    return () => controller.stop()
  }, [controller, enabled])

  // Unmount, whatever the reason: no track outlives the screen.
  useEffect(() => () => controller.detach(), [controller])

  const capturePhoto = useCallback(async () => {
    const blob = await controller.capturePhoto()
    if (blob) photoRef.current?.(blob)
    return blob
  }, [controller])

  return { camera, videoRef, capturePhoto, decodesInSoftware: controller.decodesInSoftware() }
}
